<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import McpServerForm from './McpServerForm.vue';

const props = defineProps({
  mcpServer: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['close']);
const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);

const isEdit = computed(() => !!props.mcpServer);
const i18nKey = computed(() =>
  isEdit.value ? 'CAPTAIN.MCP_SERVERS.EDIT' : 'CAPTAIN.MCP_SERVERS.CREATE'
);

const handleSubmit = async mcpServerData => {
  try {
    if (isEdit.value) {
      await store.dispatch('captainMcpServers/update', {
        id: props.mcpServer.id,
        ...mcpServerData,
      });
    } else {
      await store.dispatch('captainMcpServers/create', mcpServerData);
    }
    useAlert(t(`${i18nKey.value}.SUCCESS_MESSAGE`));
    dialogRef.value.close();
  } catch (error) {
    const errorMessage =
      parseAPIErrorResponse(error) || t(`${i18nKey.value}.ERROR_MESSAGE`);
    useAlert(errorMessage);
  }
};

const handleClose = () => {
  emit('close');
};

const handleCancel = () => {
  dialogRef.value.close();
};

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="$t(`${i18nKey}.TITLE`)"
    :description="$t('CAPTAIN.MCP_SERVERS.FORM_DESCRIPTION')"
    :show-cancel-button="false"
    :show-confirm-button="false"
    width="2xl"
    overflow-y-auto
    @close="handleClose"
  >
    <McpServerForm
      :mcp-server="mcpServer"
      @submit="handleSubmit"
      @cancel="handleCancel"
    />
    <template #footer />
  </Dialog>
</template>
